<template>
  <div class="container overflow-hidden">
    <div class="h-[30px] text-white bg-primary flex justify-end">
      <div class="text-xl flex items-center">
        <AppIcon
          v-if="!isSaved"
          icon="save"
          class="mx-[3px] cursor-pointer"
          @click="save"
        />
        <div v-if="editMode" class="border-x-2 px-1 flex items-center">
          <AppIcon
            class="cursor-pointer"
            icon="table-cells"
            @click="
              layout = 3;
              isSaved = false;
            "
          />
          <AppIcon
            class="cursor-pointer"
            icon="table-cells-large"
            @click="
              layout = 2;
              isSaved = false;
            "
          />
          <AppIcon
            class="cursor-pointer"
            icon="fa-square fa-regular"
            @click="
              layout = 1;
              isSaved = false;
            "
          />
        </div>
        <AppIcon
          class="cursor-pointer mx-[3px]"
          icon="pen-to-square"
          @click="editMode = !editMode"
        />
      </div>
    </div>
    <div :class="`w-full h-[calc(100%-30px)] gap-1 mx-1 grid grid-cols-${layout} grid-rows-${layout}`">
      <div v-for="i in layout**2"
        class="relative my-[2px] border-2 border-gray-300 overflow-hidden flex items-stretch"
        @mouseenter="!editMode && (hover = i)"
        @mouseleave="!editMode && (hover = null)"
      >
        <ExternalContent
          v-if="contentMap[i]?.externalContentId"
          :key="`external-${contentMap[i].externalContentId}`"
          :resource-id="contentMap[i].externalContentId!!"
        />
        <AppDataGrid
          v-else-if="contentMap[i]?.resourceKey"
          :key="`internal-${contentMap[i].resourceKey}`"
          row-selection="none"
          :on-row-double-clicked="() => {}"
          :resource-url="contentMap[i].uri!!"
          :resource-key="contentMap[i].resourceKey!!"
          :grid-name="contentMap[i].resourceKey!!"
					:hide-Toolbar="true"
        />
        <div v-else class="absolute inset-0 flex items-center justify-center">
          <FormKit
            v-if="hover == i"
            type="autocomplete"
            name="content"
            label="Zoeken"
            placeholder="Zoeken..."
            :options="options"
            popover
            @input="(val) => updateContentMap(i, val!!)"
          />
        </div>
        <div v-if="editMode" class="absolute inset-0 z-20 bg-gray-950/80 flex items-center justify-center flex-col">
          <FormKit
            type="autocomplete"
            name="content"
            label="Zoeken"
            placeholder="Zoeken..."
            :options="options"
            popover
            @input="(val) => {updateContentMap(i, val!!)}"
          />
          <FormKit
						v-if="contentMap[i]?.externalContentId || contentMap[i]?.resourceKey"
						type="button"
            @click="removeContentMapItem(i)"
          >
            <AppIcon
              class="cursor-pointer mx-[3px]"
              icon="fa-solid fa-trash"
            />
            Verwijderen
          </FormKit>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Resource, getAvailableResources, getMenuItems, updateUserDashboardData } from "@q-food/q-food";
import { useUserStore } from "~stores/User.ts";
import ExternalContent from "./external-content.show.vue";

const { activeUser } = storeToRefs(useUserStore());
const { success, error } = useToast();

const options = ref<OptionItem[]>([]);
const contentMap = ref<Record<number, ContentItem>>({});
const hover = ref<number | null>(null);
const layout = ref<number>(activeUser.value?.settings.dashboard.layout ?? 2);
const editMode = ref<boolean>(false);
const isSaved = ref<boolean>(true);
const availableResources = ref<Resource[]>();

interface ContentItem {
	externalContentId?: string;
	resourceKey?: string;
	uri?: string;
}

interface OptionItem {
	type: string;
	label: string;
	value: string;
}

onMounted(async () => {
	options.value = await loadOptions();
	options.value.sort((a, b) => a.label.localeCompare(b.label));
	populateContentMap();
});

async function loadOptions() {
	const [menu, resources] = await Promise.all([getMenuItems(), getAvailableResources()]);

	availableResources.value = resources;

	const internalOptions: OptionItem[] = [];
	const externalOptions: OptionItem[] = [];

	const resourceMap = new Map(resources.map((r) => [r.name, r]));

	for (const group of menu) {
		for (const child of group.children) {
			if (!child.action || !child.resource) continue;

			const resource = resourceMap.get(child.resource);
			if (!resource) continue;

			internalOptions.push({
				type: "internal",
				label: child.title,
				value: child.resource,
			});
		}
	}

	const externalGroup = menu
		.find((g) => g.id === "Externalcontent")
		?.children.filter((child) => child.action === "external-content.show" && child.props?.resourceId);

	for (const child of externalGroup ?? []) {
		externalOptions.push({
			type: "external",
			label: child.title,
			value: child.props.resourceId.toString(),
		});
	}

	return [...internalOptions, ...externalOptions];
}

function getUriForResource(resourceKey: string) {
	const resources = availableResources.value || [];
	const match = resources.find((r: Resource) => r.name === resourceKey);
	return match?.uri;
}

function save() {
	const mappedContent = Object.entries(contentMap.value).map(([index, item]) => ({
		index: index,
		...item,
	}));

	updateUserDashboardData(activeUser.value.id, {
		layout: layout.value,
		content: mappedContent,
	})
		.then(() => {
			success("Het dashboard is succesvol opgeslagen");
		})
		.catch((e) => {
			console.log(e);
			const errorMessage = e.message ?? "Er is iets misgegaan bij het updaten van het dashboard";
			error(errorMessage);
		});

	isSaved.value = true;
	editMode.value = false;
}

function populateContentMap() {
	const content = activeUser.value?.settings?.dashboard?.content;
	if (!Array.isArray(content)) return;

	for (const item of content) {
		contentMap.value[item.index] = {
			resourceKey: item.resourceKey,
			externalContentId: item.externalContentId,
			uri: item.resourceKey ? getUriForResource(item.resourceKey) : undefined,
		};
	}
}

function updateContentMap(index: number, content: string) {
	const option = options.value.find((o) => o.value === content);
	if (!option) return;

	switch (option.type) {
		case "internal":
			contentMap.value[index] = {
				resourceKey: content,
				uri: getUriForResource(content),
			};
			break;

		case "external":
			contentMap.value[index] = {
				externalContentId: content,
			};
			break;
	}

	isSaved.value = false;
}

function removeContentMapItem(index: number) {
	delete contentMap.value[index];
	isSaved.value = false;
}
</script>

<style scoped></style>
